/* cart-drawer.js */
/* global SideDrawer */

/**
 * CartDrawer
 * ----------
 * Custom element that controls the cart side drawer.
 * Listens for:
 *  - dispatch:cart-drawer:open    -> opens the drawer
 *  - dispatch:cart-drawer:close   -> closes the drawer
 *  - dispatch:cart-drawer:refresh -> refreshes cart contents
 *
 * Emits (via inherited SideDrawer or local helpers):
 *  - on:cart-drawer:before-open
 *  - on:cart:after-merge
 *
 * Usage (from anywhere in your theme):
 *  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', { detail: { opener: someButtonEl } }));
 *  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
 *  document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 */

class CartDrawer extends SideDrawer {
  constructor() {
    super();
    // Ensure methods keep instance context when used as event listeners
    this.handleDrawerOpenViaEvent = this.handleDrawerOpenViaEvent.bind(this);
    this.cartRefreshHandler = this.cartRefreshHandler.bind(this);
    this.closeDrawerViaEventHandler = this.close.bind(this, null);
    this.__initialized = false;
  }

  connectedCallback() {
    // Avoid double init if the element is re-attached
    if (this.__initialized) return;
    this.__initialized = true;

    // SideDrawer (parent) may do setup in connectedCallback; ensure it runs
    if (typeof super.connectedCallback === 'function') {
      super.connectedCallback();
    }

    this.bindEvents();

    // If drawer was rendered already open (rare), ensure images/recommendations initialize
    if (this.hasAttribute('open')) {
      this.dispatchEvent(new CustomEvent('on:cart-drawer:before-open', { bubbles: true }));
      this.initCartImagesAndCrossSells();
    }
  }

  disconnectedCallback() {
    // Remove global listeners
    document.removeEventListener('dispatch:cart-drawer:open', this.handleDrawerOpenViaEvent);
    document.removeEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.removeEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

    if (typeof super.disconnectedCallback === 'function') {
      super.disconnectedCallback();
    }
  }

  bindEvents() {
    document.addEventListener('dispatch:cart-drawer:open', this.handleDrawerOpenViaEvent);
    document.addEventListener('dispatch:cart-drawer:close', this.closeDrawerViaEventHandler);
    document.addEventListener('dispatch:cart-drawer:refresh', this.cartRefreshHandler);

    // When the drawer is about to open, lazy-load images and (re)initialize cross-sells
    this.addEventListener('on:cart-drawer:before-open', () => {
      this.initCartImagesAndCrossSells();
    });

    // After the cart content finishes merging/refreshing, re-run initializers
    this.addEventListener('on:cart:after-merge', () => {
      this.initCartImagesAndCrossSells();
    });
  }

  /**
   * Helper to (re)initialize images and cross-sell elements within the drawer.
   */
  initCartImagesAndCrossSells() {
    // Defensive: theme.manuallyLoadImages may not exist in all themes
    try {
      if (window.theme?.manuallyLoadImages) {
        window.theme.manuallyLoadImages(this);
      } else {
        // Fallback: trigger native loading for lazy images if needed
        this.querySelectorAll('img[loading="lazy"][data-src]:not([src])').forEach((img) => {
          img.src = img.getAttribute('data-src');
        });
      }
    } catch (e) {
      console?.warn?.('CartDrawer: image init warning', e);
    }

    // Initialize any cross-sell components inside the drawer
    this.querySelectorAll('cc-cart-cross-sell').forEach((el) => {
      try {
        if (typeof el.init === 'function') el.init();
      } catch (e) {
        console?.warn?.('CartDrawer: cross-sell init warning', e);
      }
    });
  }

  /**
   * Open handler when invoked via global event.
   * @param {CustomEvent} evt
   */
  handleDrawerOpenViaEvent(evt) {
    const opener = evt?.detail?.opener || null;
    // Emit a "before open" hook for any listeners
    this.dispatchEvent(new CustomEvent('on:cart-drawer:before-open', { bubbles: true }));
    // Use SideDrawer's open (handles focus trap, aria, scroll lock, etc.)
    if (typeof this.open === 'function') {
      this.open(opener);
    }
  }

  /**
   * Trigger a cart refresh; called when 'dispatch:cart-drawer:refresh' fires.
   */
  cartRefreshHandler() {
    const form = this.querySelector('cart-form');
    if (form?.refresh) {
      form.refresh();
    } else {
      // As a fallback, request a section refresh via AJAX if your theme exposes a helper.
      // You can replace this with your theme's section renderer call if needed.
      console?.warn?.('CartDrawer: cart-form.refresh() unavailable.');
    }
  }

  /**
   * Update the cart contents after an AJAX change by passing the full section HTML.
   * This is typically called by your cart form component.
   * @param {string} html - Whole-section HTML
   */
  updateFromCartChange(html) {
    const form = this.querySelector('cart-form');
    if (form?.refreshFromHtml && typeof form.refreshFromHtml === 'function') {
      form.refreshFromHtml(html);
      // Notify listeners that the merge/refresh is complete
      this.dispatchEvent(new CustomEvent('on:cart:after-merge', { bubbles: true }));
    } else {
      console?.warn?.('CartDrawer: cart-form.refreshFromHtml() unavailable.');
    }
  }
}

// Define the element once
if (!customElements.get('cart-drawer')) {
  window.customElements.define('cart-drawer', CartDrawer);
}

/* Convenience helpers (optional): expose global open/close/refresh for inline scripts */
window.CartDrawerAPI = window.CartDrawerAPI || {
  open(opener = null) {
    const el = document.querySelector('cart-drawer');
    if (!el) return;
    el.dispatchEvent(new CustomEvent('on:cart-drawer:before-open', { bubbles: true }));
    el.open?.(opener);
  },
  close() {
    const el = document.querySelector('cart-drawer');
    el?.close?.();
  },
  refresh() {
    document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh'));
  }
};
